import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { 
  Users, 
  Package, 
  RefreshCw, 
  Link2, 
  Check, 
  AlertCircle, 
  Download,
  ArrowRight,
  CheckCircle2,
  Circle
} from "lucide-react";
import { apiRequest, getApiPath } from "@/lib/queryClient";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { useToast } from "@/hooks/use-toast";
import { quickbooksImportChoices, type LikelyExisting } from "@/lib/quickbooksImportChoices";

interface QBCustomer {
  id: string;
  displayName: string;
  phone?: string;
  email?: string;
  companyName?: string;
  isActive: boolean;
}

interface QBItem {
  id: string;
  name: string;
  type: string;
  unitPrice?: number;
  description?: string;
  isActive: boolean;
}

interface Account {
  id: number;
  name: string;
  phone: string;
  quickbooksCustomerId: string | null;
}

interface Product {
  id: number;
  name: string;
  quickbooksItemId: string | null;
  categoryId: number;
  isActive: boolean;
}

interface MatchResult {
  accountId?: number;
  productId?: number;
  accountName?: string;
  productName?: string;
  qbId: string;
  qbName: string;
  matchConfidence: 'exact' | 'high' | 'partial';
}

function MultiMatchResolver({ 
  productId, 
  productName, 
  candidates, 
  onLink, 
  isLinking 
}: { 
  productId: number; 
  productName: string; 
  candidates: Array<{ qbId: string; qbName: string; qbPrice?: number; matchConfidence: 'exact' | 'high' | 'partial' }>;
  onLink: (productId: number, qbItemId: string) => void;
  isLinking: boolean;
}) {
  const [selectedQbId, setSelectedQbId] = useState<string | null>(null);
  
  return (
    <div className="border border-amber-200 rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-amber-500" />
          <span className="font-medium text-sm">{productName}</span>
        </div>
        {selectedQbId && (
          <Button
            size="sm"
            onClick={() => onLink(productId, selectedQbId)}
            disabled={isLinking}
            className="bg-green-600 hover:bg-green-700"
          >
            <Link2 className="h-3 w-3 mr-1" />
            Link Selected
          </Button>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-2">
        Select which QuickBooks item this product should be linked to:
      </p>
      <div className="space-y-1">
        {candidates.map((candidate) => (
          <label
            key={candidate.qbId}
            className={`flex items-center gap-3 p-2 rounded-md cursor-pointer border transition-colors ${
              selectedQbId === candidate.qbId 
                ? 'border-green-500 bg-green-50' 
                : 'border-gray-100 hover:bg-gray-50'
            }`}
          >
            <input
              type="radio"
              name={`match-${productId}`}
              value={candidate.qbId}
              checked={selectedQbId === candidate.qbId}
              onChange={() => setSelectedQbId(candidate.qbId)}
              className="text-green-600"
            />
            <span className="text-sm flex-1">{candidate.qbName}</span>
            {candidate.qbPrice != null && candidate.qbPrice > 0 && (
              <span className="text-xs text-gray-500">${candidate.qbPrice.toFixed(2)}</span>
            )}
            <Badge className={
              candidate.matchConfidence === 'exact' ? 'bg-green-100 text-green-700' :
              candidate.matchConfidence === 'high' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            }>
              {candidate.matchConfidence}
            </Badge>
          </label>
        ))}
      </div>
    </div>
  );
}

export function QuickBooksSync() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedCustomerMatches, setSelectedCustomerMatches] = useState<Set<string>>(new Set());
  const [selectedProductMatches, setSelectedProductMatches] = useState<Set<string>>(new Set());
  
  // State for selective import preview
  const [showCustomerImportPreview, setShowCustomerImportPreview] = useState(false);
  const [showProductImportPreview, setShowProductImportPreview] = useState(false);
  const [selectedCustomersToImport, setSelectedCustomersToImport] = useState<Set<string>>(new Set());
  const [selectedProductsToImport, setSelectedProductsToImport] = useState<Set<string>>(new Set());
  const [proposedGroupings, setProposedGroupings] = useState<Array<{
    type: 'new_group' | 'add_to_group';
    phone: string;
    existingParentId?: number;
    existingParentName?: string;
    accountIds: number[];
    accountNames: string[];
    newAccountIds: number[];
  }>>([]);
  const [showGroupingDialog, setShowGroupingDialog] = useState(false);

  const { data: authStatus, isLoading: authLoading } = useQuery<{ isAuthenticated: boolean; hasValidToken: boolean }>({
    queryKey: ["/api/quickbooks/auth-status"],
    refetchInterval: false,
  });

  const isConnected = authStatus?.isAuthenticated && authStatus?.hasValidToken;

  const { data: customerSyncData, isLoading: customerSyncLoading, error: customerSyncError, refetch: refetchCustomerSync } = useQuery<{
    qbCustomers: QBCustomer[];
    freshOrderAccounts: Account[];
    linkedCount: number;
    unlinkedCount: number;
    availableToImport: number;
    likelyExisting?: LikelyExisting[];
    suggestedMatches: MatchResult[];
  }>({
    queryKey: [getApiPath("/api/quickbooks/sync/customers")],
    queryFn: async () => {
      const response = await fetch(getApiPath("/api/quickbooks/sync/customers"), { credentials: "include" });
      if (response.status === 503) {
        const errorData = await response.json();
        if (errorData.requireReconnect) {
          throw new Error("QB_RECONNECT_REQUIRED");
        }
      }
      if (!response.ok) throw new Error("Failed to load sync data");
      return response.json();
    },
    enabled: !!isConnected,
    retry: (failureCount, error: any) => {
      if (error?.message === "QB_RECONNECT_REQUIRED") return false;
      return failureCount < 3;
    },
  });

  const { data: productSyncData, isLoading: productSyncLoading, error: productSyncError, refetch: refetchProductSync } = useQuery<{
    qbItems: QBItem[];
    freshOrderProducts: Product[];
    linkedCount: number;
    unlinkedCount: number;
    availableToImport: number;
    likelyExisting?: LikelyExisting[];
    suggestedMatches: MatchResult[];
    multiMatchProducts?: Array<{
      productId: number;
      productName: string;
      candidates: Array<{ qbId: string; qbName: string; qbPrice?: number; matchConfidence: 'exact' | 'high' | 'partial' }>;
    }>;
  }>({
    queryKey: [getApiPath("/api/quickbooks/sync/products")],
    queryFn: async () => {
      const response = await fetch(getApiPath("/api/quickbooks/sync/products"), { credentials: "include" });
      if (response.status === 503) {
        const errorData = await response.json();
        if (errorData.requireReconnect) {
          throw new Error("QB_RECONNECT_REQUIRED");
        }
      }
      if (!response.ok) throw new Error("Failed to load sync data");
      return response.json();
    },
    enabled: !!isConnected,
    retry: (failureCount, error: any) => {
      if (error?.message === "QB_RECONNECT_REQUIRED") return false;
      return failureCount < 3;
    },
  });

  const autoMatchCustomersMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/quickbooks/sync/auto-match-customers");
      if (!response.ok) throw new Error("Failed to auto-match customers");
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Customers Linked",
        description: `Successfully linked ${data.matchedCount} customers to QuickBooks`,
      });
      queryClient.invalidateQueries({ queryKey: [getApiPath("/api/quickbooks/sync/customers")] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to auto-match customers",
        variant: "destructive",
      });
    },
  });

  const autoMatchProductsMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/quickbooks/sync/auto-match-products");
      if (!response.ok) throw new Error("Failed to auto-match products");
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Products Linked",
        description: `Successfully linked ${data.matchedCount} products to QuickBooks`,
      });
      queryClient.invalidateQueries({ queryKey: [getApiPath("/api/quickbooks/sync/products")] });
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to auto-match products",
        variant: "destructive",
      });
    },
  });

  const linkCustomerMutation = useMutation({
    mutationFn: async ({ accountId, qbCustomerId }: { accountId: number; qbCustomerId: string }) => {
      const response = await apiRequest("PATCH", `/api/accounts/${accountId}`, {
        quickbooksCustomerId: qbCustomerId,
      });
      if (!response.ok) throw new Error("Failed to link customer");
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Customer Linked",
        description: "Successfully linked customer to QuickBooks",
      });
      queryClient.invalidateQueries({ queryKey: [getApiPath("/api/quickbooks/sync/customers")] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to link customer",
        variant: "destructive",
      });
    },
  });

  const linkProductMutation = useMutation({
    mutationFn: async ({ productId, qbItemId }: { productId: number; qbItemId: string }) => {
      const response = await apiRequest("PATCH", `/api/products/${productId}`, {
        quickbooksItemId: qbItemId,
      });
      if (!response.ok) throw new Error("Failed to link product");
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Product Linked",
        description: "Successfully linked product to QuickBooks",
      });
      queryClient.invalidateQueries({ queryKey: [getApiPath("/api/quickbooks/sync/products")] });
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: apiErrorMessage(error, "Failed to link product"),
        variant: "destructive",
      });
    },
  });

  const importCustomersMutation = useMutation({
    mutationFn: async (customerIds?: string[]) => {
      const response = await apiRequest("POST", "/api/quickbooks/sync/import-customers", {
        customerIds: customerIds || [],
      });
      if (!response.ok) throw new Error("Failed to import customers");
      return response.json();
    },
    onSuccess: (data) => {
      if (data.importedCount === 0) {
        toast({
          title: data.skippedCount > 0 ? "Nothing Imported" : "No Customers Found",
          description: data.message || "No customers available to import from QuickBooks",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Customers Imported",
          description: data.skippedCount > 0
            ? data.message
            : `Successfully imported ${data.importedCount} customers from QuickBooks`,
        });
      }
      setShowCustomerImportPreview(false);
      setSelectedCustomersToImport(new Set());
      queryClient.invalidateQueries({ queryKey: [getApiPath("/api/quickbooks/sync/customers")] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });

      if (data.proposedGroupings && data.proposedGroupings.length > 0) {
        setProposedGroupings(data.proposedGroupings);
        setShowGroupingDialog(true);
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to import customers from QuickBooks",
        variant: "destructive",
      });
    },
  });

  const applyGroupingsMutation = useMutation({
    mutationFn: async (groupings: typeof proposedGroupings) => {
      const response = await apiRequest("POST", "/api/quickbooks/sync/apply-groupings", {
        groupings,
      });
      if (!response.ok) throw new Error("Failed to apply groupings");
      return response.json();
    },
    onSuccess: (data) => {
      const parts: string[] = [];
      if (data.groupsCreated > 0) parts.push(`${data.groupsCreated} new group(s) created`);
      if (data.accountsAdded > 0) parts.push(`${data.accountsAdded} account(s) added to existing groups`);
      toast({
        title: "Accounts Grouped",
        description: parts.join(', ') || "Groupings applied successfully",
      });
      setShowGroupingDialog(false);
      setProposedGroupings([]);
      queryClient.invalidateQueries({ queryKey: [getApiPath("/api/quickbooks/sync/customers")] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to apply account groupings",
        variant: "destructive",
      });
    },
  });

  const importProductsMutation = useMutation({
    mutationFn: async (productIds?: string[]) => {
      const response = await apiRequest("POST", "/api/quickbooks/sync/import-products", {
        itemIds: productIds || [],
      });
      if (!response.ok) throw new Error("Failed to import products");
      return response.json();
    },
    onSuccess: (data) => {
      if (data.importedCount === 0) {
        toast({
          title: data.skippedCount > 0 ? "Nothing Imported" : "No Products Found",
          description: data.message || "No products available to import from QuickBooks",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Products Imported",
          description: data.skippedCount > 0
            ? data.message
            : `Successfully imported ${data.importedCount} products from QuickBooks`,
        });
      }
      setShowProductImportPreview(false);
      setSelectedProductsToImport(new Set());
      queryClient.invalidateQueries({ queryKey: [getApiPath("/api/quickbooks/sync/products")] });
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to import products from QuickBooks",
        variant: "destructive",
      });
    },
  });

  if (authLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          Checking QuickBooks connection...
        </CardContent>
      </Card>
    );
  }

  if (!isConnected) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            QuickBooks Data Sync
          </CardTitle>
          <CardDescription>
            Link your FreshOrder customers and products with QuickBooks for accurate invoicing
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Connect to QuickBooks above to enable data sync. This will allow you to link your existing customers and products for seamless invoicing.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const customerProgress = customerSyncData 
    ? Math.round((customerSyncData.linkedCount / (customerSyncData.linkedCount + customerSyncData.unlinkedCount)) * 100) || 0
    : 0;
  
  const productProgress = productSyncData 
    ? Math.round((productSyncData.linkedCount / (productSyncData.linkedCount + productSyncData.unlinkedCount)) * 100) || 0
    : 0;

  // What each import list offers. A QuickBooks record with the name of an
  // unlinked FreshOrder record is not offered at all (it is linked, under
  // Suggested Matches); a looser match is listed but left unchecked.
  const customerImport = quickbooksImportChoices(
    customerSyncData?.qbCustomers ?? [],
    (customerSyncData?.freshOrderAccounts ?? []).map(a => a.quickbooksCustomerId),
    customerSyncData?.likelyExisting,
  );
  const productImport = quickbooksImportChoices(
    productSyncData?.qbItems ?? [],
    (productSyncData?.freshOrderProducts ?? []).map(p => p.quickbooksItemId),
    productSyncData?.likelyExisting,
  );

  // Only what a list still offers is imported: linking a row under Suggested
  // Matches takes it out of the list, and out of the selection with it.
  const customersSelectedToImport = customerImport.offered.filter(c => selectedCustomersToImport.has(c.id)).map(c => c.id);
  const productsSelectedToImport = productImport.offered.filter(p => selectedProductsToImport.has(p.id)).map(p => p.id);

  const importPromptText = (noun: 'customers' | 'products', newCount: number, possibleMatchCount: number) => {
    if (possibleMatchCount === 0) {
      return `${newCount} additional ${noun} in QuickBooks not yet in FreshOrder.`;
    }
    if (newCount === 0) {
      return `${possibleMatchCount} QuickBooks ${noun} may already be in FreshOrder under a similar name. Link them above, or review them in the import list.`;
    }
    return `${newCount} additional ${noun} in QuickBooks not yet in FreshOrder. ${possibleMatchCount} more may already be here under a similar name: link them above, or review them in the import list.`;
  };

  const getMatchBadgeColor = (confidence: string) => {
    switch (confidence) {
      case 'exact': return 'bg-green-100 text-green-800';
      case 'high': return 'bg-blue-100 text-blue-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="h-5 w-5" />
          QuickBooks Data Sync
        </CardTitle>
        <CardDescription>
          Link your existing QuickBooks customers and products
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="customers" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="customers" className="flex items-center gap-2" data-testid="tab-sync-customers">
              <Users className="h-4 w-4" />
              Customers
              {customerSyncData && customerSyncData.unlinkedCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {customerSyncData.unlinkedCount} unlinked
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2" data-testid="tab-sync-products">
              <Package className="h-4 w-4" />
              Products
              {productSyncData && productSyncData.unlinkedCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {productSyncData.unlinkedCount} unlinked
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="customers" className="space-y-4">
            {customerSyncLoading ? (
              <div className="text-center py-8 text-gray-500">Loading customer data...</div>
            ) : (customerSyncError as any)?.message === "QB_RECONNECT_REQUIRED" ? (
              <Alert variant="destructive" className="border-amber-300 bg-amber-50">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  QuickBooks connection appears broken. Please disconnect and reconnect QuickBooks above to restore syncing.
                </AlertDescription>
              </Alert>
            ) : !customerSyncData ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>Failed to load customer sync data</AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Sync Progress</p>
                      <p className="text-xs text-gray-500">
                        {customerSyncData.linkedCount} of {customerSyncData.linkedCount + customerSyncData.unlinkedCount} customers linked
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => refetchCustomerSync()}
                        data-testid="button-refresh-customers"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                      </Button>
                    </div>
                  </div>
                  <Progress value={customerProgress} className="h-2" />
                  
                  {/* Linked Customers section - moved directly under progress */}
                  {customerSyncData.linkedCount > 0 && (
                    <div className="pt-3 border-t border-gray-200">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        Linked Customers ({customerSyncData.linkedCount})
                      </h4>
                      <div className="bg-white rounded-lg p-3 max-h-48 overflow-y-auto border border-gray-100">
                        <ul className="space-y-1">
                          {customerSyncData.freshOrderAccounts
                            .filter(a => a.quickbooksCustomerId)
                            .map(account => (
                              <li key={account.id} className="flex items-center gap-2 text-sm">
                                <Check className="h-3 w-3 text-green-600 flex-shrink-0" />
                                <span>{account.name}</span>
                                <span className="text-gray-400 text-xs">({account.phone})</span>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Linking comes before importing: an account that is already here is
                    linked to its QuickBooks customer, never imported a second time. */}
                {customerSyncData.suggestedMatches.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium">Suggested Matches</h4>
                        <p className="text-xs text-gray-500">
                          Select which matches to link. Uncheck items you want to skip.
                        </p>
                      </div>
                      {selectedCustomerMatches.size > 0 && (
                        <Button
                          size="sm"
                          onClick={() => {
                            selectedCustomerMatches.forEach(key => {
                              const match = customerSyncData.suggestedMatches.find(
                                m => `${m.accountId}-${m.qbId}` === key
                              );
                              if (match) {
                                linkCustomerMutation.mutate({
                                  accountId: match.accountId!,
                                  qbCustomerId: match.qbId,
                                });
                              }
                            });
                            setSelectedCustomerMatches(new Set());
                          }}
                          disabled={linkCustomerMutation.isPending}
                          className="bg-cn-brand-green hover:bg-green-600"
                          data-testid="button-link-selected-customers"
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Link Selected ({selectedCustomerMatches.size})
                        </Button>
                      )}
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">
                            <Checkbox
                              checked={selectedCustomerMatches.size === customerSyncData.suggestedMatches.length}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedCustomerMatches(new Set(
                                    customerSyncData.suggestedMatches.map(m => `${m.accountId}-${m.qbId}`)
                                  ));
                                } else {
                                  setSelectedCustomerMatches(new Set());
                                }
                              }}
                              data-testid="checkbox-select-all-customers"
                            />
                          </TableHead>
                          <TableHead className="w-[180px]">FreshOrder Customer</TableHead>
                          <TableHead className="w-[40px]"></TableHead>
                          <TableHead className="w-[180px]">QuickBooks Customer</TableHead>
                          <TableHead className="w-[80px]">Match</TableHead>
                          <TableHead className="w-[80px]">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {customerSyncData.suggestedMatches.map((match) => {
                          const matchKey = `${match.accountId}-${match.qbId}`;
                          return (
                            <TableRow key={matchKey}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedCustomerMatches.has(matchKey)}
                                  onCheckedChange={(checked) => {
                                    const newSet = new Set(selectedCustomerMatches);
                                    if (checked) {
                                      newSet.add(matchKey);
                                    } else {
                                      newSet.delete(matchKey);
                                    }
                                    setSelectedCustomerMatches(newSet);
                                  }}
                                  data-testid={`checkbox-customer-${match.accountId}`}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{match.accountName}</TableCell>
                              <TableCell>
                                <ArrowRight className="h-4 w-4 text-gray-400" />
                              </TableCell>
                              <TableCell>{match.qbName}</TableCell>
                              <TableCell>
                                <Badge className={getMatchBadgeColor(match.matchConfidence)}>
                                  {match.matchConfidence}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => linkCustomerMutation.mutate({
                                    accountId: match.accountId!,
                                    qbCustomerId: match.qbId,
                                  })}
                                  disabled={linkCustomerMutation.isPending}
                                  data-testid={`button-link-customer-${match.accountId}`}
                                >
                                  <Link2 className="h-3 w-3 mr-1" />
                                  Link
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {showCustomerImportPreview && customerImport.offered.length > 0 ? (
                  (() => {
                    // QB customers not already imported, less those already in
                    // FreshOrder under the same name
                    const unimportedCustomers = customerImport.offered;
                    return (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-medium">Select Customers to Import</h4>
                            <p className="text-xs text-gray-500">
                              Review and select which QuickBooks customers to import ({unimportedCustomers.length} available)
                            </p>
                            {customerImport.sameNameCount > 0 && (
                              <p className="text-xs text-gray-500" data-testid="text-import-customers-same-name">
                                {customerImport.sameNameCount} not listed: already in FreshOrder under the same name. Link them under Suggested Matches above.
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShowCustomerImportPreview(false);
                                setSelectedCustomersToImport(new Set());
                              }}
                            >
                              Cancel
                            </Button>
                            {customersSelectedToImport.length > 0 && (
                              <Button
                                size="sm"
                                onClick={() => importCustomersMutation.mutate(customersSelectedToImport)}
                                disabled={importCustomersMutation.isPending}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid="button-import-selected-customers"
                              >
                                {importCustomersMutation.isPending ? (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    Importing...
                                  </>
                                ) : (
                                  <>
                                    <Download className="h-4 w-4 mr-2" />
                                    Import Selected ({customersSelectedToImport.length})
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[50px]">
                                <Checkbox
                                  checked={customerImport.selectAllIds.length > 0 && customerImport.selectAllIds.every(id => selectedCustomersToImport.has(id))}
                                  disabled={customerImport.selectAllIds.length === 0}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      // Possible matches stay unchecked: imported only on purpose
                                      setSelectedCustomersToImport(new Set(customerImport.selectAllIds));
                                    } else {
                                      setSelectedCustomersToImport(new Set());
                                    }
                                  }}
                                  data-testid="checkbox-select-all-import-customers"
                                />
                              </TableHead>
                              <TableHead>Customer Name</TableHead>
                              <TableHead>Company</TableHead>
                              <TableHead>Phone</TableHead>
                              <TableHead>Email</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {unimportedCustomers.map((customer) => (
                              <TableRow key={customer.id}>
                                <TableCell>
                                  <Checkbox
                                    checked={selectedCustomersToImport.has(customer.id)}
                                    onCheckedChange={(checked) => {
                                      const newSet = new Set(selectedCustomersToImport);
                                      if (checked) {
                                        newSet.add(customer.id);
                                      } else {
                                        newSet.delete(customer.id);
                                      }
                                      setSelectedCustomersToImport(newSet);
                                    }}
                                    data-testid={`checkbox-import-customer-${customer.id}`}
                                  />
                                </TableCell>
                                <TableCell className="font-medium">
                                  {customer.displayName}
                                  {customerImport.possibleMatchFor(customer.id) && (
                                    <Badge className="ml-2 bg-yellow-100 text-yellow-800" data-testid={`badge-possible-match-customer-${customer.id}`}>
                                      Possible match: {customerImport.possibleMatchFor(customer.id)!.existingName}
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-gray-500">{customer.companyName || '-'}</TableCell>
                                <TableCell className="text-gray-500">{customer.phone || '-'}</TableCell>
                                <TableCell className="text-gray-500">{customer.email || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    );
                  })()
                ) : customerSyncData.linkedCount === 0 && customerSyncData.unlinkedCount === 0 ? (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 text-center space-y-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                      <Users className="h-8 w-8 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">No Customers Yet</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Get started by importing your customers from QuickBooks
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        refetchCustomerSync();
                        setShowCustomerImportPreview(true);
                      }}
                      disabled={customerSyncLoading}
                      size="lg"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                      data-testid="button-preview-import-customers"
                    >
                      {customerSyncLoading ? (
                        <>
                          <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                          Loading QuickBooks Customers...
                        </>
                      ) : (
                        <>
                          <Download className="h-5 w-5 mr-2" />
                          Preview &amp; Import Customers from QuickBooks
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500">
                      You'll be able to review and select which customers to import
                    </p>
                  </div>
                ) : customerSyncData.suggestedMatches.length === 0 && customerSyncData.unlinkedCount === 0 && customerSyncData.availableToImport === 0 ? (
                  <Alert className="border-green-200 bg-green-50">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700">
                      All customers are linked to QuickBooks! Invoices will use direct ID lookups for accurate billing.
                    </AlertDescription>
                  </Alert>
                ) : customerImport.offered.length > 0 && !showCustomerImportPreview ? (
                  <div className="space-y-4">
                    <Alert className="border-blue-200 bg-blue-50">
                      <Download className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-700" data-testid="text-import-customers-prompt">
                        {importPromptText('customers', customerSyncData.availableToImport, customerImport.possibleMatchCount)}
                      </AlertDescription>
                    </Alert>
                    <Button
                      onClick={() => {
                        refetchCustomerSync();
                        setShowCustomerImportPreview(true);
                      }}
                      disabled={customerSyncLoading}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="button-import-more-customers"
                    >
                      {customerSyncLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Import More Customers from QuickBooks
                        </>
                      )}
                    </Button>
                  </div>
                ) : customerSyncData.suggestedMatches.length > 0 ? null : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {customerSyncData.unlinkedCount} unlinked customers found but no automatic matches detected. 
                      You can manually link them or they will be matched by name during invoice creation.
                    </AlertDescription>
                  </Alert>
                )}

              </>
            )}
          </TabsContent>

          <TabsContent value="products" className="space-y-4">
            {productSyncLoading ? (
              <div className="text-center py-8 text-gray-500">Loading product data...</div>
            ) : (productSyncError as any)?.message === "QB_RECONNECT_REQUIRED" ? (
              <Alert variant="destructive" className="border-amber-300 bg-amber-50">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  QuickBooks connection appears broken. Please disconnect and reconnect QuickBooks above to restore syncing.
                </AlertDescription>
              </Alert>
            ) : !productSyncData ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>Failed to load product sync data</AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Sync Progress</p>
                      <p className="text-xs text-gray-500">
                        {productSyncData.linkedCount} of {productSyncData.linkedCount + productSyncData.unlinkedCount} products linked
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => refetchProductSync()}
                        data-testid="button-refresh-products"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                      </Button>
                    </div>
                  </div>
                  <Progress value={productProgress} className="h-2" />
                  
                  {/* Linked Products section - directly under progress */}
                  {productSyncData.linkedCount > 0 && (
                    <div className="pt-3 border-t border-gray-200">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        Linked Products ({productSyncData.linkedCount})
                      </h4>
                      <div className="bg-white rounded-lg p-3 max-h-48 overflow-y-auto border border-gray-100">
                        <ul className="space-y-1">
                          {productSyncData.freshOrderProducts
                            .filter(p => p.quickbooksItemId)
                            .map(product => (
                              <li key={product.id} className="flex items-center gap-2 text-sm">
                                <Check className="h-3 w-3 text-green-600 flex-shrink-0" />
                                <span>{product.name}</span>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {productSyncData.multiMatchProducts && productSyncData.multiMatchProducts.length > 0 && (
                  <div className="space-y-3 mt-4">
                    <Alert className="border-amber-200 bg-amber-50">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-700">
                        <strong>{productSyncData.multiMatchProducts.length} product{productSyncData.multiMatchProducts.length > 1 ? 's' : ''} need{productSyncData.multiMatchProducts.length === 1 ? 's' : ''} your attention.</strong>{' '}
                        Multiple QuickBooks items match these products. Select the correct one to link.
                      </AlertDescription>
                    </Alert>
                    <div className="space-y-3">
                      {productSyncData.multiMatchProducts.map((issue) => (
                        <MultiMatchResolver
                          key={issue.productId}
                          productId={issue.productId}
                          productName={issue.productName}
                          candidates={issue.candidates}
                          onLink={(productId, qbItemId) => {
                            linkProductMutation.mutate({ productId, qbItemId });
                          }}
                          isLinking={linkProductMutation.isPending}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Linking comes before importing, as for customers. */}
                {productSyncData.suggestedMatches.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium">Suggested Matches</h4>
                        <p className="text-xs text-gray-500">
                          Select which matches to link. Uncheck items you want to skip.
                        </p>
                      </div>
                      {selectedProductMatches.size > 0 && (
                        <Button
                          size="sm"
                          onClick={() => {
                            selectedProductMatches.forEach(key => {
                              const match = productSyncData.suggestedMatches.find(
                                m => `${m.productId}-${m.qbId}` === key
                              );
                              if (match) {
                                linkProductMutation.mutate({
                                  productId: match.productId!,
                                  qbItemId: match.qbId,
                                });
                              }
                            });
                            setSelectedProductMatches(new Set());
                          }}
                          disabled={linkProductMutation.isPending}
                          className="bg-cn-brand-green hover:bg-green-600"
                          data-testid="button-link-selected-products"
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Link Selected ({selectedProductMatches.size})
                        </Button>
                      )}
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">
                            <Checkbox
                              checked={selectedProductMatches.size === productSyncData.suggestedMatches.length}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedProductMatches(new Set(
                                    productSyncData.suggestedMatches.map(m => `${m.productId}-${m.qbId}`)
                                  ));
                                } else {
                                  setSelectedProductMatches(new Set());
                                }
                              }}
                              data-testid="checkbox-select-all-products"
                            />
                          </TableHead>
                          <TableHead className="w-[180px]">FreshOrder Product</TableHead>
                          <TableHead className="w-[40px]"></TableHead>
                          <TableHead className="w-[180px]">QuickBooks Item</TableHead>
                          <TableHead className="w-[80px]">Match</TableHead>
                          <TableHead className="w-[80px]">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {productSyncData.suggestedMatches.map((match) => {
                          const matchKey = `${match.productId}-${match.qbId}`;
                          return (
                            <TableRow key={matchKey}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedProductMatches.has(matchKey)}
                                  onCheckedChange={(checked) => {
                                    const newSet = new Set(selectedProductMatches);
                                    if (checked) {
                                      newSet.add(matchKey);
                                    } else {
                                      newSet.delete(matchKey);
                                    }
                                    setSelectedProductMatches(newSet);
                                  }}
                                  data-testid={`checkbox-product-${match.productId}`}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{match.productName}</TableCell>
                              <TableCell>
                                <ArrowRight className="h-4 w-4 text-gray-400" />
                              </TableCell>
                              <TableCell>{match.qbName}</TableCell>
                              <TableCell>
                                <Badge className={getMatchBadgeColor(match.matchConfidence)}>
                                  {match.matchConfidence}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => linkProductMutation.mutate({
                                    productId: match.productId!,
                                    qbItemId: match.qbId,
                                  })}
                                  disabled={linkProductMutation.isPending}
                                  data-testid={`button-link-product-${match.productId}`}
                                >
                                  <Link2 className="h-3 w-3 mr-1" />
                                  Link
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {productSyncData.linkedCount === 0 && productSyncData.unlinkedCount === 0 ? (
                  showProductImportPreview && productSyncData.qbItems.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium">Select Products to Import</h4>
                          <p className="text-xs text-gray-500">
                            Review and select which QuickBooks products to import
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setShowProductImportPreview(false);
                              setSelectedProductsToImport(new Set());
                            }}
                          >
                            Cancel
                          </Button>
                          {selectedProductsToImport.size > 0 && (
                            <Button
                              size="sm"
                              onClick={() => importProductsMutation.mutate(Array.from(selectedProductsToImport))}
                              disabled={importProductsMutation.isPending}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid="button-import-selected-products"
                            >
                              {importProductsMutation.isPending ? (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                  Importing...
                                </>
                              ) : (
                                <>
                                  <Download className="h-4 w-4 mr-2" />
                                  Import Selected ({selectedProductsToImport.size})
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[50px]">
                              <Checkbox
                                checked={selectedProductsToImport.size === productSyncData.qbItems.filter(p => p.isActive).length}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedProductsToImport(new Set(
                                      productSyncData.qbItems.filter(p => p.isActive).map(p => p.id)
                                    ));
                                  } else {
                                    setSelectedProductsToImport(new Set());
                                  }
                                }}
                                data-testid="checkbox-select-all-import-products"
                              />
                            </TableHead>
                            <TableHead>Product Name</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Price</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {productSyncData.qbItems.map((product) => (
                            <TableRow key={product.id} className={!product.isActive ? 'opacity-50' : ''}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedProductsToImport.has(product.id)}
                                  onCheckedChange={(checked) => {
                                    const newSet = new Set(selectedProductsToImport);
                                    if (checked) {
                                      newSet.add(product.id);
                                    } else {
                                      newSet.delete(product.id);
                                    }
                                    setSelectedProductsToImport(newSet);
                                  }}
                                  data-testid={`checkbox-import-product-${product.id}`}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{product.name}</TableCell>
                              <TableCell className="text-gray-500">{product.type}</TableCell>
                              <TableCell className="text-gray-500">
                                {product.unitPrice ? `$${product.unitPrice.toFixed(2)}` : '-'}
                              </TableCell>
                              <TableCell>
                                <Badge variant={product.isActive ? 'default' : 'secondary'}>
                                  {product.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 text-center space-y-4">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                        <Package className="h-8 w-8 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">No Products Yet</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Get started by importing your products from QuickBooks
                        </p>
                      </div>
                      <Button
                        onClick={() => {
                          refetchProductSync();
                          setShowProductImportPreview(true);
                        }}
                        disabled={productSyncLoading}
                        size="lg"
                        className="bg-green-600 hover:bg-green-700 text-white px-8"
                        data-testid="button-preview-import-products"
                      >
                        {productSyncLoading ? (
                          <>
                            <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                            Loading QuickBooks Products...
                          </>
                        ) : (
                          <>
                            <Download className="h-5 w-5 mr-2" />
                            Preview &amp; Import Products from QuickBooks
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-gray-500">
                        You'll be able to review and select which products to import
                      </p>
                    </div>
                  )
                ) : productSyncData.suggestedMatches.length === 0 && productSyncData.unlinkedCount === 0 && productSyncData.availableToImport === 0 ? (
                  <Alert className="border-green-200 bg-green-50">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700">
                      All products are linked to QuickBooks! Invoices will use accurate item IDs for proper tax tracking.
                    </AlertDescription>
                  </Alert>
                ) : productImport.offered.length > 0 && !showProductImportPreview ? (
                  <div className="space-y-4">
                    <Alert className="border-green-200 bg-green-50">
                      <Download className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-700" data-testid="text-import-products-prompt">
                        {importPromptText('products', productSyncData.availableToImport, productImport.possibleMatchCount)}
                      </AlertDescription>
                    </Alert>
                    <Button
                      onClick={() => {
                        refetchProductSync();
                        setShowProductImportPreview(true);
                      }}
                      disabled={productSyncLoading}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="button-import-more-products"
                    >
                      {productSyncLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Import More Products from QuickBooks
                        </>
                      )}
                    </Button>
                  </div>
                ) : productImport.offered.length > 0 && showProductImportPreview ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium">Select Products to Import</h4>
                        <p className="text-xs text-gray-500">
                          Review and select which QuickBooks products to import
                        </p>
                        {productImport.sameNameCount > 0 && (
                          <p className="text-xs text-gray-500" data-testid="text-import-products-same-name">
                            {productImport.sameNameCount} not listed: already in FreshOrder under the same name. Link them above.
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowProductImportPreview(false);
                            setSelectedProductsToImport(new Set());
                          }}
                        >
                          Cancel
                        </Button>
                        {productsSelectedToImport.length > 0 && (
                          <Button
                            size="sm"
                            onClick={() => importProductsMutation.mutate(productsSelectedToImport)}
                            disabled={importProductsMutation.isPending}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid="button-import-selected-products-more"
                          >
                            {importProductsMutation.isPending ? (
                              <>
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                Importing...
                              </>
                            ) : (
                              <>
                                <Download className="h-4 w-4 mr-2" />
                                Import Selected ({productsSelectedToImport.length})
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">
                            <Checkbox
                              checked={productImport.selectAllIds.length > 0 && productImport.selectAllIds.every(id => selectedProductsToImport.has(id))}
                              disabled={productImport.selectAllIds.length === 0}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  // Possible matches stay unchecked: imported only on purpose
                                  setSelectedProductsToImport(new Set(productImport.selectAllIds));
                                } else {
                                  setSelectedProductsToImport(new Set());
                                }
                              }}
                              data-testid="checkbox-select-all-import-more-products"
                            />
                          </TableHead>
                          <TableHead>Product Name</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {productImport.offered
                          .map((product) => (
                            <TableRow key={product.id} className={!product.isActive ? 'opacity-50' : ''}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedProductsToImport.has(product.id)}
                                  onCheckedChange={(checked) => {
                                    const newSet = new Set(selectedProductsToImport);
                                    if (checked) {
                                      newSet.add(product.id);
                                    } else {
                                      newSet.delete(product.id);
                                    }
                                    setSelectedProductsToImport(newSet);
                                  }}
                                  data-testid={`checkbox-import-more-product-${product.id}`}
                                />
                              </TableCell>
                              <TableCell className="font-medium">
                                {product.name}
                                {productImport.possibleMatchFor(product.id) && (
                                  <Badge className="ml-2 bg-yellow-100 text-yellow-800" data-testid={`badge-possible-match-product-${product.id}`}>
                                    Possible match: {productImport.possibleMatchFor(product.id)!.existingName}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-gray-500">{product.type}</TableCell>
                              <TableCell className="text-gray-500">
                                {product.unitPrice ? `$${product.unitPrice.toFixed(2)}` : '-'}
                              </TableCell>
                              <TableCell>
                                <Badge variant={product.isActive ? 'default' : 'secondary'}>
                                  {product.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : productSyncData.suggestedMatches.length > 0 ? null : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {productSyncData.unlinkedCount} unlinked products found but no automatic matches detected.
                      Products without QuickBooks links will be matched by name during invoice creation.
                    </AlertDescription>
                  </Alert>
                )}

              </>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      <Dialog open={showGroupingDialog} onOpenChange={(open) => {
        if (!open) {
          setShowGroupingDialog(false);
          setProposedGroupings([]);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Group Accounts with Matching Phone Numbers?
            </DialogTitle>
            <DialogDescription>
              Some imported customers share the same phone number as existing accounts.
              Would you like to group them together?
              <span className="block mt-1 text-xs text-gray-400">Grouped accounts keep their individual orders and invoices but are accessible with the same phone number and PIN.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto py-2">
            {proposedGroupings.map((grouping, idx) => {
              const existingNames: string[] = [];
              const newNames: string[] = [];
              grouping.accountIds.forEach((id, i) => {
                if (grouping.newAccountIds.includes(id)) {
                  newNames.push(grouping.accountNames[i]);
                } else {
                  existingNames.push(grouping.accountNames[i]);
                }
              });
              return (
                <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="text-xs">
                      {grouping.phone}
                    </Badge>
                    {grouping.type === 'add_to_group' ? (
                      <Badge className="bg-blue-100 text-blue-700 text-xs">Add to existing group</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700 text-xs">New group</Badge>
                    )}
                  </div>
                  {grouping.type === 'add_to_group' && grouping.existingParentName && (
                    <p className="text-sm text-gray-600 mb-1">
                      Existing group: <span className="font-medium">{grouping.existingParentName}</span>
                    </p>
                  )}
                  <div className="text-sm space-y-1">
                    {existingNames.length > 0 && (
                      <p>
                        <span className="text-gray-500">Existing: </span>
                        <span className="font-medium">{existingNames.join(', ')}</span>
                      </p>
                    )}
                    {newNames.length > 0 && (
                      <p>
                        <span className="text-gray-500">Imported: </span>
                        <span className="font-medium text-green-700">{newNames.join(', ')}</span>
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowGroupingDialog(false);
                setProposedGroupings([]);
              }}
            >
              Skip Grouping
            </Button>
            <Button
              onClick={() => applyGroupingsMutation.mutate(proposedGroupings)}
              disabled={applyGroupingsMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {applyGroupingsMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Grouping...
                </>
              ) : (
                <>
                  <Users className="h-4 w-4 mr-2" />
                  Group Accounts
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
